import React from 'react';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { rem } from 'polished';
import { Text } from '../Text';

const StyledProjectThumbnail = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: ${rem(`12px`)};
  margin-right: 0;
  transform: scale(1);
  transition: box-shadow 0.8s, transform 0.6s;
  pointer-event: none;
  flex: 1 0 auto;
  width: 100%;
  &:last-child {
    margin-bottom: 0;
    ${({ theme }) => theme.breakpoints.phablet`
      margin-right: 0;
      margin-bottom: 0;
    `}
  }
  ${({ theme }) => theme.breakpoints.desktop`
    &:hover {
      cursor: pointer;
      transform: scale(1.1);
      .project-thumbnail__wrapper {
        box-shadow: 0px 15px 20px -14px rgba(246, 56, 251, 0.5), 0px -15px 20px -14px rgba(246, 56, 251, 0.5);
      }
      .project-thumbnail__container::after {
        opacity: 1;
        transform: rotate(-45deg) translateY(100%);
      }
    }
  `}
  .project-thumbnail__main {
    display: flex;
    flex-wrap: wrap;
    flex: 1 0;
    color: ${({ theme }) => theme.colors.white_1};
  }
  .project-thumbnail__wrapper {
    display: flex;
    pointer-event: all;
    flex: 1 0 100%;
    flex-wrap: wrap;
    padding: 1px;
    margin: 1px;
    border-radius: ${rem(`12px`)};
    position: relative;
    box-sizing: border-box;
    background-clip: padding-box;
    border: solid 1px transparent;
    transition: all 0.5s ease;
    background: ${({ theme }) => `linear-gradient(
      265deg,
      ${theme.colors.blue_1_65},
      ${theme.colors.blue_1},
      ${theme.colors.blue_1_90}
    )`};
    ::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: inherit;
      margin: -2px;
      z-index: -1;
      background: ${({ theme }) => `linear-gradient(
          -30deg,
          ${theme.colors.blue_1_30},
          ${theme.colors.blue_1_65},
          ${theme.colors.pink_2}
        )`};
    }
  }

  .project-thumbnail__container {
    padding: ${rem(`6px`)};
    border-radius: ${rem(`12px`)};
    margin: -2px;
    display: flex;
    flex: 1 0 auto;
    justify-content: center;
    overflow: hidden;
    position: relative;
    width: 100%;
    ::after {
      content: '';
      position: absolute;
      top: -100%;
      left: -100%;
      width: 270%;
      height: 270%;
      background: linear-gradient(
        0deg,
        transparent,
        transparent 30%,
        rgba(246, 56, 251, 0.3)
      );
      transform: rotate(-45deg);
      transition: all 0.5s ease;
      opacity: 0;
    }
  }
  .project-thumbnail__footer {
    display: flex;
    flex: 1 0 100%;
    align-items: center;
    justify-content: center;
    pointer-event: all;
    height: ${({ theme }) => theme.rhythmSizing(4)}rem;
    z-index: 99;
  }
  .project-thumbnail__image {
    align-items: center;
    justify-content: center;
    pointer-events: all;
    position: relative;
  }
`;

export interface IProjectThumbnail {
  className?: string;
  title: string;
  image: any;
  alt: string;
  linkTo?: string;
}

export const ProjectThumbnail = ({
  className,
  title,
  image,
  alt,
  linkTo,
}: IProjectThumbnail) => (
  <StyledProjectThumbnail className={className}>
    <div className="project-thumbnail__main">
      <div className="project-thumbnail__wrapper">
        <div className="project-thumbnail__container">
          {linkTo ? (
            <Link aria-label={alt} to={linkTo}>
              <GatsbyImage
                image={image}
                alt={alt}
                className="project-thumbnail__image"
              />
            </Link>
          ) : (
            <GatsbyImage
              image={image}
              alt={alt}
              className="project-thumbnail__image"
            />
          )}
        </div>
      </div>
      <div className="project-thumbnail__footer">
        <Text noRhythem type="body1" tag="span">
          {title}
        </Text>
      </div>
    </div>
  </StyledProjectThumbnail>
);
